import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from 'sonner';
import {
  Package, Search, RefreshCw, Eye, ChevronLeft, ChevronRight,
  Ban, Pencil, Loader2, FileText, AlertTriangle, FileSpreadsheet, CheckCircle2,
} from 'lucide-react';
import { DocRefLink } from '../../components/DocRefLink';
import RegularizacionRecepcionModal from '../../components/RegularizacionRecepcionModal';

const fmtCLP = (n) => (Number(n) || 0).toLocaleString('es-CL');
const fmtFecha = (s) => {
  if (!s) return '';
  // Soporta ISO YYYY-MM-DD y "MM/DD/YY 0" legacy
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  return s;
};

export default function RecepcionesListPage() {
  const { user } = useAuth();
  const role = (user?.rol || user?.role || '').toLowerCase();
  const canManage = ['admin', 'administrador', 'gerencia', 'bodega_admin', 'jefe_bodega'].includes(role);
  // Iter 291.p — Regularización post-Alegra solo para admin/gerencia
  const canRegularizar = ['admin', 'administrador', 'gerencia'].includes(role);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, activas: 0, anuladas: 0, monto_total_activas: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [fechaDesde, setFechaDesde] = useState('2026-01-01');
  const [fechaHasta, setFechaHasta] = useState('');
  const [estado, setEstado] = useState('activas'); // activas | anuladas | todos
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Dialogs
  const [anularDlg, setAnularDlg] = useState(null);
  const [anularMotivo, setAnularMotivo] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [editDlg, setEditDlg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [pdfDlg, setPdfDlg] = useState(null);
  // Iter 291.p — modal de regularización post-Alegra
  const [regularizacionDlg, setRegularizacionDlg] = useState(null);

  const fetchRecepciones = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * pageSize);
      params.append('limit', pageSize);
      if (q) params.append('q', q);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (estado && estado !== 'todos') params.append('estado', estado);
      const res = await api.get(`/compras/recepciones?${params}`);
      setItems(res.data.recepciones || []);
      setStats({
        total: res.data.total || 0,
        activas: res.data.activas || 0,
        anuladas: res.data.anuladas || 0,
        monto_total_activas: res.data.monto_total_activas || 0,
      });
    } catch {
      toast.error('Error al cargar recepciones');
    } finally {
      setLoading(false);
    }
  }, [q, fechaDesde, fechaHasta, estado, page]);

  useEffect(() => { fetchRecepciones(); }, [fetchRecepciones]);

  const abrirAnular = (r) => { setAnularDlg(r); setAnularMotivo(''); };
  const confirmarAnular = async () => {
    if ((anularMotivo || '').trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setAnulando(true);
    try {
      const res = await api.post(`/bodega/recepciones/${anularDlg.numero_recepcion}/anular`, {
        motivo: anularMotivo.trim(),
      });
      toast.success(res.data?.message || 'Recepción anulada');
      setAnularDlg(null);
      setAnularMotivo('');
      await fetchRecepciones();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al anular recepción');
    } finally { setAnulando(false); }
  };

  const abrirEditar = async (r) => {
    try {
      const res = await api.get(`/compras/recepciones/${r.numero_recepcion}`);
      const items_dlg = (res.data?.detalles || []).map((d) => ({
        item: d.item,
        descripcion: d.descripcion || '',
        codigo_material: d.codigo_material || '',
        cantidad_recepcionada: d.cantidad_recepcionada || 0,
        valor_unitario: d.valor_unitario || 0,
        ot_numero: d.ot_numero || null,
      }));
      setEditDlg({ ...r, items: items_dlg, motivo: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar detalle');
    }
  };
  const confirmarEditar = async () => {
    setEditing(true);
    try {
      const res = await api.put(`/bodega/recepciones/${editDlg.numero_recepcion}`, {
        items: editDlg.items.map((it) => ({
          item: it.item,
          cantidad_recepcionada: Number(it.cantidad_recepcionada) || 0,
          valor_unitario: Number(it.valor_unitario) || 0,
          codigo_material: it.codigo_material || '',
          descripcion: it.descripcion || '',
          ot_numero: it.ot_numero || null,
        })),
        motivo: editDlg.motivo || '',
      });
      toast.success(res.data?.message || 'Recepción editada');
      setEditDlg(null);
      await fetchRecepciones();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al editar recepción');
    } finally { setEditing(false); }
  };

  const verPDF = (numero) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/compras/recepciones/${numero}/pdf`;
    setPdfDlg({ url, numero });
  };

  const exportarExcel = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', 5000);
      if (q) params.append('q', q);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (estado && estado !== 'todos') params.append('estado', estado);
      const res = await api.get(`/compras/recepciones?${params}`);
      const all = res.data.recepciones || [];
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(all.map((r) => ({
        'N° Recepción': r.numero_recepcion,
        'Fecha': fmtFecha(r.fecha_iso || r.fecha),
        'Proveedor': r.nombre_proveedor || r.rut_proveedor || '',
        'RUT Proveedor': r.rut_proveedor || '',
        'OC': r.numero_oc,
        'Tipo Doc': r.tipo_documento || r.tipodoc || '',
        'Doc N°': r.docmto || r.numero_factura || '',
        'Responsable': r.responsable || r.encargado || '',
        'Monto': r.monto || 0,
        'Estado': r.anulada ? 'ANULADA' : 'Activa',
        'Motivo anulación': r.motivo_anulacion || '',
        'Anulada por': r.anulada_por || '',
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Recepciones');
      XLSX.writeFile(wb, `recepciones_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      toast.error('Error al exportar a Excel');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            Listado de Recepciones
          </h1>
          <p className="text-sm text-muted-foreground">
            {stats.total} recepcion(es){q ? ` que coinciden con "${q}"` : ''} · Monto activas: <strong>${fmtCLP(stats.monto_total_activas)}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchRecepciones} data-testid="btn-refresh-recepciones">
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={exportarExcel}
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-excel-recepciones">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <button onClick={() => { setEstado('todos'); setPage(0); }}
          className={`text-left p-3 rounded-lg border bg-white transition-all ${
            estado === 'todos' ? 'ring-2 ring-slate-400 border-slate-300' : 'hover:border-gray-300'
          }`}
          data-testid="kpi-todos">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Total</p>
          <p className="text-xl font-bold text-gray-900">{stats.total.toLocaleString('es-CL')}</p>
        </button>
        <button onClick={() => { setEstado('activas'); setPage(0); }}
          className={`text-left p-3 rounded-lg border bg-emerald-50/60 transition-all ${
            estado === 'activas' ? 'ring-2 ring-emerald-400 border-emerald-300' : 'hover:border-emerald-200'
          }`}
          data-testid="kpi-activas">
          <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Activas
          </p>
          <p className="text-xl font-bold text-emerald-700">{stats.activas.toLocaleString('es-CL')}</p>
          <p className="text-[10px] text-emerald-600">${fmtCLP(stats.monto_total_activas)}</p>
        </button>
        <button onClick={() => { setEstado('anuladas'); setPage(0); }}
          className={`text-left p-3 rounded-lg border bg-red-50/60 transition-all ${
            estado === 'anuladas' ? 'ring-2 ring-red-400 border-red-300' : 'hover:border-red-200'
          }`}
          data-testid="kpi-anuladas">
          <p className="text-[10px] font-semibold text-red-700 uppercase tracking-wider flex items-center gap-1">
            <Ban className="w-3 h-3" /> Anuladas
          </p>
          <p className="text-xl font-bold text-red-700">{stats.anuladas.toLocaleString('es-CL')}</p>
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Buscar</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="N° recepción, OC, folio, proveedor…"
                className="pl-9 h-9"
                data-testid="input-search-recepciones"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Desde</Label>
            <Input type="date" className="h-9 w-[160px] mt-1" value={fechaDesde}
              onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
              data-testid="fecha-desde" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Hasta</Label>
            <Input type="date" className="h-9 w-[160px] mt-1" value={fechaHasta}
              onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
              data-testid="fecha-hasta" />
          </div>
          <Button variant="ghost" size="sm" onClick={() => { setQ(''); setFechaDesde('2026-01-01'); setFechaHasta(''); setEstado('activas'); setPage(0); }}
            data-testid="btn-clear-filters">Limpiar</Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4"><TableSkeleton rows={8} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Sin recepciones que coincidan con los filtros.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-recepciones">
                <thead className="bg-muted text-xs text-left">
                  <tr>
                    <th className="px-3 py-2">N° Recepción</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Proveedor</th>
                    <th className="px-3 py-2">OC</th>
                    <th className="px-3 py-2">Doc</th>
                    <th className="px-3 py-2 text-right">Monto</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const anulada = !!r.anulada;
                    return (
                      <tr key={r.numero_recepcion}
                        className={`border-t hover:bg-slate-50/50 transition-colors ${anulada ? 'bg-red-50/30' : ''}`}
                        data-testid={`row-${r.numero_recepcion}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`font-mono font-semibold ${anulada ? 'line-through text-red-700' : 'text-slate-800'}`}>
                              {r.numero_recepcion}
                            </span>
                            {anulada && (
                              <span className="text-[9px] bg-red-100 text-red-700 border border-red-300 px-1 rounded font-bold"
                                title={r.motivo_anulacion ? `Motivo: ${r.motivo_anulacion} · por ${r.anulada_por || 's/d'}` : ''}>
                                ANULADA
                              </span>
                            )}
                          </div>
                          {anulada && r.motivo_anulacion && (
                            <div className="mt-1 flex items-start gap-1 text-[10px] leading-tight text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-1 max-w-[280px]"
                              data-testid={`motivo-anulacion-${r.numero_recepcion}`}>
                              <AlertTriangle className="w-3 h-3 shrink-0 mt-[1px]" />
                              <div className="min-w-0">
                                <div className="font-semibold uppercase tracking-wide text-[9px]">Motivo anulación</div>
                                <div className="italic break-words">{r.motivo_anulacion}</div>
                                <div className="text-[9px] text-red-600/80 mt-0.5">
                                  por {r.anulada_por?.split('@')[0] || 's/d'}
                                  {r.fecha_anulacion ? ` · ${fmtFecha(r.fecha_anulacion.slice(0, 10))}` : ''}
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{fmtFecha(r.fecha_iso || r.fecha)}</td>
                        <td className="px-3 py-2 text-xs max-w-[220px]">
                          <span className="block truncate" title={r.nombre_proveedor || r.rut_proveedor || ''}>
                            {r.nombre_proveedor || <span className="text-muted-foreground">{r.rut_proveedor || '—'}</span>}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.numero_oc
                            ? <DocRefLink tipo="oc" numero={r.numero_oc} label={r.numero_oc} testId={`recep-oc-link-${r.numero_recepcion}`} />
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {(r.tipo_documento || '').toUpperCase()}{' '}
                          {(r.docmto || r.numero_factura)
                            ? <DocRefLink tipo="dte" folio={r.docmto || r.numero_factura} rut={r.rut_proveedor} label={r.docmto || r.numero_factura} icon={false} testId={`recep-doc-link-${r.numero_recepcion}`} />
                            : ''}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">${fmtCLP(Math.round(r.monto || 0))}</td>
                        <td className="px-3 py-2">
                          {anulada ? (
                            <span className="text-[10px] text-red-700 font-semibold">Anulada · {r.anulada_por?.split('@')[0] || ''}</span>
                          ) : r.regularizacion_abierta ? (
                            <span className="text-[10px] text-amber-800 font-semibold flex items-center gap-1" title="Regularización abierta">
                              <AlertTriangle className="w-3 h-3" />
                              Reg. abierta
                            </span>
                          ) : (
                            <span className="text-[10px] text-emerald-700 font-semibold">Activa</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button type="button" onClick={() => verPDF(r.numero_recepcion)}
                              className="p-1.5 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                              title="Ver PDF de recepción"
                              data-testid={`btn-pdf-${r.numero_recepcion}`}>
                              <Eye className="w-4 h-4" />
                            </button>
                            {canManage && !anulada && (
                              <>
                                <button type="button" onClick={() => abrirEditar(r)}
                                  className="p-1.5 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                                  title="Editar cantidades / valores"
                                  data-testid={`btn-edit-${r.numero_recepcion}`}>
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => abrirAnular(r)}
                                  className="p-1.5 rounded hover:bg-red-100 text-red-600 transition-colors"
                                  title="Anular recepción (revertir stock)"
                                  data-testid={`btn-anular-${r.numero_recepcion}`}>
                                  <Ban className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {canRegularizar && !anulada && (
                              <button type="button" onClick={() => setRegularizacionDlg(r)}
                                className={`p-1.5 rounded transition-colors ${
                                  r.regularizacion_abierta
                                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-800 ring-1 ring-amber-400'
                                    : 'hover:bg-amber-100 text-amber-700'
                                }`}
                                title={r.regularizacion_abierta
                                  ? `Regularización abierta — vence ${new Date(r.regularizacion_expira_at).toLocaleString('es-CL')}`
                                  : 'Regularizar (bypass Alegra, 24h)'}
                                data-testid={`btn-regularizar-${r.numero_recepcion}`}>
                                <AlertTriangle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {stats.total > pageSize && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Página {page + 1} de {Math.ceil(stats.total / pageSize)}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              data-testid="btn-prev-page">
              <ChevronLeft className="w-4 h-4" /> Anterior
            </Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= stats.total} onClick={() => setPage((p) => p + 1)}
              data-testid="btn-next-page">
              Siguiente <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* PDF Dialog */}
      <Dialog open={!!pdfDlg} onOpenChange={(o) => { if (!o) setPdfDlg(null); }}>
        <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 pt-4 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5 text-blue-600" /> Recepción {pdfDlg?.numero}
            </DialogTitle>
          </DialogHeader>
          <iframe title="Recepción PDF" src={pdfDlg?.url} className="flex-1 w-full" />
        </DialogContent>
      </Dialog>

      {/* Anular Dialog */}
      <Dialog open={!!anularDlg} onOpenChange={(o) => { if (!o) { setAnularDlg(null); setAnularMotivo(''); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-anular">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" /> Anular Recepción
            </DialogTitle>
            <DialogDescription>
              Vas a anular <strong>{anularDlg?.numero_recepcion}</strong> (OC <strong>{anularDlg?.numero_oc}</strong>).
              Se <strong>revertirá el stock</strong> sumado, se liberará la factura en la OC, y quedará registrado en auditoría.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              ⚠ No se podrá anular si la factura ya está contabilizada en Alegra/ERP.
            </div>
            <div>
              <Label htmlFor="motivo" className="text-xs font-medium">Motivo (mín. 5 caracteres)</Label>
              <Textarea id="motivo" rows={3} value={anularMotivo}
                onChange={(e) => setAnularMotivo(e.target.value)}
                placeholder="Ej: cantidad incorrecta, devolución, recepción duplicada…"
                className="mt-1 text-sm"
                data-testid="input-motivo-anular" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnularDlg(null)} disabled={anulando}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarAnular}
              disabled={anulando || (anularMotivo || '').trim().length < 5}
              data-testid="btn-confirm-anular">
              {anulando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
              Anular Recepción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar Dialog */}
      <Dialog open={!!editDlg} onOpenChange={(o) => { if (!o) setEditDlg(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-editar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Pencil className="w-5 h-5" /> Editar Recepción {editDlg?.numero_recepcion}
            </DialogTitle>
            <DialogDescription>
              Modificá cantidades y valores. El sistema ajustará automáticamente el stock y recalculará el monto total.
            </DialogDescription>
          </DialogHeader>
          {editDlg && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                ⚠ No se podrá guardar si la factura asociada ya está contabilizada en Alegra/ERP.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-2 py-1.5 w-10">#</th>
                      <th className="px-2 py-1.5">Descripción</th>
                      <th className="px-2 py-1.5 w-28">Cant.</th>
                      <th className="px-2 py-1.5 w-32">Valor Unit.</th>
                      <th className="px-2 py-1.5 w-28 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editDlg.items.map((it, idx) => {
                      const total = (Number(it.cantidad_recepcionada) || 0) * (Number(it.valor_unitario) || 0);
                      return (
                        <tr key={`edit-${idx}`} className="border-t">
                          <td className="px-2 py-1.5">{it.item}</td>
                          <td className="px-2 py-1.5">
                            <span className="block truncate max-w-xs">{it.descripcion}</span>
                            {it.codigo_material && <span className="text-[10px] text-muted-foreground font-mono">{it.codigo_material}</span>}
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" min="0" value={it.cantidad_recepcionada}
                              onChange={(e) => {
                                const next = [...editDlg.items];
                                next[idx] = { ...it, cantidad_recepcionada: e.target.value };
                                setEditDlg({ ...editDlg, items: next });
                              }}
                              className="h-8 text-xs"
                              data-testid={`input-cant-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" min="0" value={it.valor_unitario}
                              onChange={(e) => {
                                const next = [...editDlg.items];
                                next[idx] = { ...it, valor_unitario: e.target.value };
                                setEditDlg({ ...editDlg, items: next });
                              }}
                              className="h-8 text-xs"
                              data-testid={`input-vu-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">${fmtCLP(Math.round(total))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-bold bg-muted/30">
                      <td colSpan={4} className="px-2 py-2 text-right">Total recepción</td>
                      <td className="px-2 py-2 text-right font-mono">
                        ${fmtCLP(Math.round(editDlg.items.reduce((s, it) => s + ((Number(it.cantidad_recepcionada) || 0) * (Number(it.valor_unitario) || 0)), 0)))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div>
                <Label htmlFor="motivo-edit" className="text-xs font-medium">Motivo del cambio (opcional)</Label>
                <Input id="motivo-edit" value={editDlg.motivo}
                  onChange={(e) => setEditDlg({ ...editDlg, motivo: e.target.value })}
                  placeholder="Ej: corrección de cantidad por error de digitación"
                  className="mt-1 text-sm"
                  data-testid="input-motivo-editar" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(null)} disabled={editing}>Cancelar</Button>
            <Button onClick={confirmarEditar} disabled={editing} data-testid="btn-confirm-editar">
              {editing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!canManage && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-2">
          <AlertTriangle className="w-3.5 h-3.5" />
          Solo admin, gerencia o jefe de bodega pueden anular o editar recepciones.
        </div>
      )}

      {/* Iter 291.p — Regularización de recepción post-Alegra (admin/gerencia) */}
      <RegularizacionRecepcionModal
        open={!!regularizacionDlg}
        recepcion={regularizacionDlg}
        onClose={() => setRegularizacionDlg(null)}
        onChanged={() => fetchRecepciones()}
      />
    </div>
  );
}
